import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Mail, Copy, Check, Building, Zap, Wifi, Shield, Home, ExternalLink, Lock, Phone, Tv } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';

export const EmailTemplatesPage = () => {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language === 'es';
  const [copiedId, setCopiedId] = useState(null);
  
  // États pour les champs de formulaire (optionnels)
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    conseiller: '',
    numeroContrat: '',
    numeroCompte: '',
    numeroPolice: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const generateEmail = (template) => {
    let content = template;
    // Remplacer les placeholders par les valeurs du formulaire ou garder les placeholders
    content = content.replace(/\[Votre prénom\]/g, formData.prenom || '[Votre prénom]');
    content = content.replace(/\[Votre nom\]/g, formData.nom || '[Votre nom]');
    content = content.replace(/\[Tu nombre\]/g, formData.prenom || '[Tu nombre]');
    content = content.replace(/\[Tu apellido\]/g, formData.nom || '[Tu apellido]');
    content = content.replace(/\[Nom du conseiller\]/g, formData.conseiller || '[Nom du conseiller]');
    content = content.replace(/\[Nombre del asesor\]/g, formData.conseiller || '[Nombre del asesor]');
    content = content.replace(/\[Votre N° de Contrat\]/g, formData.numeroContrat || '[Votre N° de Contrat]');
    content = content.replace(/\[Tu Número de Contrato\]/g, formData.numeroContrat || '[Tu Número de Contrato]');
    content = content.replace(/\[Numéro de compte\]/g, formData.numeroCompte || '[Numéro de compte]');
    content = content.replace(/\[Número de cuenta\]/g, formData.numeroCompte || '[Número de cuenta]');
    content = content.replace(/\[Numéro de police\]/g, formData.numeroPolice || '[Numéro de police]');
    content = content.replace(/\[Número de póliza\]/g, formData.numeroPolice || '[Número de póliza]');
    content = content.replace(/\[Votre prénom et nom\]/g, 
      (formData.prenom && formData.nom) ? `${formData.prenom} ${formData.nom}` : '[Votre prénom et nom]'
    );
    content = content.replace(/\[Tu nombre y apellido\]/g, 
      (formData.prenom && formData.nom) ? `${formData.prenom} ${formData.nom}` : '[Tu nombre y apellido]'
    );
    return content;
  };

  const templatesData = isSpanish ? {
    title: 'Modelos de Email para Negociar',
    subtitle: 'Modelos de cartas listos para usar. Rellena los campos opcionales para personalizar',
    templates: [
      {
        id: 'banco',
        category: 'Banco',
        icon: Building,
        color: 'from-green-500 to-emerald-500',
        title: 'Renegociar Comisiones Bancarias',
        subject: 'Solicitud de resumen y optimización de mis comisiones bancarias',
        content: `Hola [Nombre del asesor],

Con el fin de optimizar la gestión de mis finanzas personales, quisiera obtener un resumen detallado de las comisiones que pago cada mes por el conjunto de mis productos bancarios (comisiones de mantenimiento de cuenta, comisiones diversas, seguros vinculados a mis tarjetas, etc.).

En este contexto, ¿podrían indicarme:
- si existe una opción gratuita o de menor coste para mi cuenta corriente,
- cuáles son las posibilidades para reducir mis comisiones vinculadas a las tarjetas bancarias y de mantenimiento de cuenta,
- las libretas de ahorro reglamentadas y garantizadas por el Estado que podría abrir,
- si soy elegible para ciertos productos reservados a hogares con ingresos modestos.

Quisiera seguir manteniendo mi relación con un banco tradicional, pero únicamente si las tarifas siguen siendo competitivas. Preferiría evitar tener que migrar a un banco en línea gratuito, aunque se convierta en una opción si mis comisiones actuales no pueden reducirse.

El objetivo es reducir mis gastos fijos, asegurando al mismo tiempo mis ahorros en soportes simples, garantizados y sin riesgo.

Gracias de antemano por su respuesta detallada,

Atentamente,
[Tu nombre y apellido]`,
        tips: 'Sé específico sobre tus necesidades. Los bancos valoran la claridad y los clientes que se informan.'
      },
      {
        id: 'electricidad',
        category: 'Electricidad',
        icon: Zap,
        color: 'from-yellow-500 to-orange-500',
        title: 'Renegociar Electricidad',
        subject: 'Solicitud de revisión de mi contrato de electricidad',
        content: `Hola [Nombre del asesor],

Les contacto porque actualmente atravieso un período en el que necesito reducir mis gastos mensuales. Tras comparar, he encontrado ofertas de electricidad a tarifas más competitivas que la mía.

Estando satisfecho con sus servicios hasta ahora, me gustaría saber si pueden proponerme una revisión de mi tarifa o un ajuste de mi contrato, para que pueda seguir siendo cliente suyo.

Gracias de antemano por su respuesta,

Atentamente,
[Tu nombre y apellido]`,
        tips: 'Menciona tu satisfacción con el servicio actual. Los proveedores prefieren retener buenos clientes.'
      },
      {
        id: 'internet',
        category: 'Internet',
        icon: Wifi,
        color: 'from-blue-500 to-cyan-500',
        title: 'Renegociar Internet',
        subject: 'Solicitud de revisión de mi contrato de internet',
        content: `Hola [Nombre del asesor],

Les contacto porque debo reducir mis gastos mensuales. Tras comparar, he encontrado ofertas de internet a tarifas más competitivas que la mía.

Estando satisfecho con sus servicios hasta ahora, me gustaría saber si pueden proponerme una revisión de mi tarifa o un ajuste de mi contrato, para poder continuar siendo cliente suyo.

Gracias de antemano por su respuesta,

Atentamente,
[Tu nombre y apellido]`,
        tips: 'Las compañías de internet son muy competitivas. Mencionar otras ofertas suele funcionar.'
      },
      {
        id: 'seguro-auto',
        category: 'Seguro Auto',
        icon: Shield,
        color: 'from-red-500 to-rose-500',
        title: 'Renegociar Seguro Auto/Moto',
        subject: 'Solicitud de revisión de mi contrato de seguro auto/moto',
        content: `Hola [Nombre del asesor],

Constato un aumento de mi prima de seguro este año. Tras haber comparado con otras compañías, he encontrado ofertas equivalentes a una tarifa inferior.

Deseando quedarme con ustedes, les escribo para saber si pueden alinear o ajustar mi cotización. En caso contrario, me veré obligado a rescindir mi contrato.

Gracias por su comprensión y por su respuesta rápida,

Atentamente,
[Tu nombre y apellido]`,
        tips: 'Menciona tu intención de quedarte pero también tu disposición a cambiar. Es clave para negociar.'
      },
      {
        id: 'seguro-hogar',
        category: 'Seguro Hogar',
        icon: Home,
        color: 'from-purple-500 to-pink-500',
        title: 'Renegociar Seguro Hogar',
        subject: 'Solicitud de revisión de mi contrato de seguro hogar',
        content: `Hola [Nombre del asesor],

Les contacto porque atravieso un período en el que debo reducir mis gastos mensuales. Tras comparar, he encontrado ofertas de seguro hogar a tarifas más competitivas que la mía.

Estando satisfecho con sus servicios hasta ahora, me gustaría saber si pueden proponerme una revisión de mi tarifa o un ajuste de mi contrato, para poder seguir asegurado con ustedes.

Gracias de antemano por su respuesta,

Atentamente,
[Tu nombre y apellido]`,
        tips: 'La competencia es fuerte en seguros hogar. Mencionar tu satisfacción ayuda a negociar.'
      },
      {
        id: 'movil',
        category: 'Móvil',
        icon: Phone,
        color: 'from-indigo-500 to-blue-500',
        title: 'Renegociar Tarifa Móvil',
        subject: 'Solicitud de revisión de mi tarifa móvil',
        content: `Hola [Nombre del asesor],

Les contacto porque debo reducir mis gastos mensuales. Tras comparar, he encontrado tarifas móviles a precios más competitivos que la mía.

Estando satisfecho con sus servicios hasta ahora, me gustaría saber si pueden proponerme una revisión de mi tarifa o un ajuste de mi plan, para poder continuar siendo cliente suyo.

Gracias de antemano por su respuesta,

Atentamente,
[Tu nombre y apellido]`,
        tips: 'El mercado móvil es muy competitivo. Los operadores prefieren mantener a sus clientes con mejores ofertas.'
      },
      {
        id: 'suscripciones',
        category: 'Suscripciones',
        icon: Tv,
        color: 'from-pink-500 to-rose-500',
        title: 'Renegociar Suscripciones',
        subject: 'Solicitud de revisión de mi suscripción',
        content: `Hola [Nombre del asesor],

Les contacto porque atravieso un período en el que debo reducir mis gastos mensuales. Tras comparar, he encontrado ofertas de suscripción (streaming, gimnasio, etc.) a tarifas más competitivas que la mía.

Estando satisfecho con sus servicios hasta ahora, me gustaría saber si pueden proponerme una revisión de mi tarifa o un ajuste de mi contrato, para poder seguir suscrito con ustedes.

Gracias de antemano por su respuesta,

Atentamente,
[Tu nombre y apellido]`,
        tips: 'Las suscripciones son a menudo negociables. Menciona tu antigüedad y satisfacción.'
      }
    ]
  } : {
    title: 'Modèles d\'Email pour Négocier',
    subtitle: 'Modèles de lettres prêts à l\'emploi. Remplissez les champs optionnels pour personnaliser',
    templates: [
      {
        id: 'banque',
        category: 'Banque',
        icon: Building,
        color: 'from-green-500 to-emerald-500',
        title: 'Renégocier Frais Bancaires',
        subject: 'Demande de récapitulatif et d\'optimisation de mes frais bancaires',
        content: `Bonjour [Nom du conseiller],

Afin d'optimiser la gestion de mes finances personnelles, je souhaiterais obtenir un récapitulatif détaillé des frais que je paie chaque mois pour l'ensemble de mes produits bancaires (frais de tenue de compte, commissions diverses, assurances liées à mes cartes, etc.).

Dans ce cadre, pourriez-vous également m'indiquer :
- s'il existe une option gratuite ou à moindre coût pour mon compte courant (ou pour d'autres comptes de base),
- quelles sont les possibilités pour réduire mes frais liés aux cartes bancaires et aux frais de tenue de compte,
- les livrets d'épargne réglementés et garantis par l'État que je pourrais ouvrir (Livret A, LDDS, LEP, etc.),
- si je suis éligible à certains produits réservés aux ménages modestes, comme le Livret d'Épargne Populaire.

Je tiens à préciser que je préfère conserver une relation avec une banque traditionnelle, mais uniquement si les tarifs restent compétitifs. J'aimerais éviter de devoir migrer vers une banque en ligne gratuite, même si cela devient une option si mes frais actuels ne peuvent pas être réduits.

L'objectif est de réduire mes frais fixes, tout en sécurisant mes économies sur des supports simples, garantis et sans risque.

Merci d'avance pour votre retour détaillé,

Bien cordialement,
[Votre prénom et nom]`,
        tips: 'Soyez précis sur vos besoins. Les banques apprécient les clients qui s\'informent et sont clairs.'
      },
      {
        id: 'electricite',
        category: 'Électricité',
        icon: Zap,
        color: 'from-yellow-500 to-orange-500',
        title: 'Renégocier Électricité',
        subject: 'Demande de révision de mon contrat d\'électricité',
        content: `Bonjour [Nom du conseiller],

Je vous contacte car je traverse une période où je dois réduire mes dépenses mensuelles. Après comparaison, j'ai trouvé des offres d'électricité à des tarifs plus compétitifs que le mien.

Étant satisfait de vos services jusqu'ici, j'aimerais savoir si vous pouvez me proposer une révision de mon tarif ou un ajustement de mon contrat, afin que je puisse continuer à rester client chez vous.

Merci d'avance pour votre retour,

Bien cordialement,
[Votre prénom et nom]`,
        tips: 'Mentionnez votre satisfaction du service actuel. Les fournisseurs préfèrent garder les bons clients.'
      },
      {
        id: 'internet',
        category: 'Internet',
        icon: Wifi,
        color: 'from-blue-500 to-cyan-500',
        title: 'Renégocier Internet',
        subject: 'Demande de révision de mon contrat internet',
        content: `Bonjour [Nom du conseiller],

Je vous contacte car je dois réduire mes dépenses mensuelles. Après comparaison, j'ai trouvé des offres internet à des tarifs plus compétitifs que le mien.

Étant satisfait de vos services jusqu'ici, j'aimerais savoir si vous pouvez me proposer une révision de mon tarif ou un ajustement de mon contrat, afin que je puisse continuer à rester client chez vous.

Merci d'avance pour votre retour,

Bien cordialement,
[Votre prénom et nom]`,
        tips: 'Les opérateurs internet sont très compétitifs. Mentionner d\'autres offres fonctionne bien.'
      },
      {
        id: 'assurance-auto',
        category: 'Assurance Auto',
        icon: Shield,
        color: 'from-red-500 to-rose-500',
        title: 'Renégocier Assurance Auto/Moto',
        subject: 'Demande de révision de mon contrat d\'assurance auto/moto',
        content: `Bonjour [Nom du conseiller],

Je constate une augmentation de ma prime d'assurance cette année. Après avoir comparé avec d'autres compagnies, j'ai trouvé des offres équivalentes à un tarif inférieur.

Souhaitant rester chez vous, je vous écris afin de savoir si vous pouvez aligner ou ajuster ma cotisation. Dans le cas contraire, je serai contraint de résilier mon contrat.

Merci de votre compréhension et de votre retour rapide,

Bien cordialement,
[Votre prénom et nom]`,
        tips: 'Mentionnez votre volonté de rester mais aussi votre disposition à partir. C\'est la clé de la négociation.'
      },
      {
        id: 'assurance-habitation',
        category: 'Assurance Habitation',
        icon: Home,
        color: 'from-purple-500 to-pink-500',
        title: 'Renégocier Assurance Habitation',
        subject: 'Demande de révision de mon contrat d\'assurance habitation',
        content: `Bonjour [Nom du conseiller],

Je vous contacte car je traverse une période où je dois réduire mes dépenses mensuelles. Après comparaison, j'ai trouvé des offres d'assurance habitation à des tarifs plus compétitifs que le mien.

Étant satisfait de vos services jusqu'ici, j'aimerais savoir si vous pouvez me proposer une révision de mon tarif ou un ajustement de mon contrat, afin que je puisse continuer à rester assuré chez vous.

Merci d'avance pour votre retour,

Bien cordialement,
[Votre prénom et nom]`,
        tips: 'La concurrence est forte en assurance habitation. Mentionner votre satisfaction aide à négocier.'
      },
      {
        id: 'mobile',
        category: 'Forfait Mobile',
        icon: Phone,
        color: 'from-indigo-500 to-blue-500',
        title: 'Renégocier Forfait Mobile',
        subject: 'Demande de révision de mon forfait mobile',
        content: `Bonjour [Nom du conseiller],

Je vous contacte car je dois réduire mes dépenses mensuelles. Après comparaison, j'ai trouvé des forfaits mobiles à des tarifs plus compétitifs que le mien.

Étant satisfait de vos services jusqu'ici, j'aimerais savoir si vous pouvez me proposer une révision de mon tarif ou un ajustement de mon forfait, afin que je puisse continuer à rester client chez vous.

Merci d'avance pour votre retour,

Bien cordialement,
[Votre prénom et nom]`,
        tips: 'Le marché mobile est très concurrentiel. Les opérateurs préfèrent garder leurs clients avec de meilleures offres.'
      },
      {
        id: 'abonnements',
        category: 'Abonnements',
        icon: Tv,
        color: 'from-pink-500 to-rose-500',
        title: 'Renégocier Abonnements',
        subject: 'Demande de révision de mon abonnement',
        content: `Bonjour [Nom du conseiller],

Je vous contacte car je traverse une période où je dois réduire mes dépenses mensuelles. Après comparaison, j'ai trouvé des offres d'abonnement (streaming, salle de sport, etc.) à des tarifs plus compétitifs que le mien.

Étant satisfait de vos services jusqu'ici, j'aimerais savoir si vous pouvez me proposer une révision de mon tarif ou un ajustement de mon contrat, afin que je puisse continuer à rester abonné chez vous.

Merci d'avance pour votre retour,

Bien cordialement,
[Votre prénom et nom]`,
        tips: 'Les abonnements sont souvent négociables. Mentionnez votre ancienneté et votre satisfaction.'
      }
    ]
  };

  const copyToClipboard = async (text, id) => {
    try {
      const generatedText = generateEmail(text);
      await navigator.clipboard.writeText(generatedText);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-white">
      <SEOHead 
        title={isSpanish ? "Modelos de Email - SAVEA" : "Modèles d'Email - SAVEA"}
        description={isSpanish ? "Modelos de cartas listos para negociar tus contratos" : "Modèles de lettres prêts à l'emploi pour renégocier vos contrats"}
      />
      
      {/* Hero Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-r from-sage-600 to-sage-700">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Mail className="w-10 h-10" />
              <Badge className="bg-white/20 text-white border-white/30">
                {isSpanish ? '7 Modelos' : '7 Modèles'}
              </Badge>
            </div>
            <h1 className="text-3xl lg:text-5xl font-bold mb-4">
              {templatesData.title}
            </h1>
            <p className="text-xl text-sage-100 max-w-3xl mx-auto">
              {templatesData.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Privacy Banner */}
      <section className="py-6 bg-green-50 border-y-2 border-green-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-3 text-green-800">
            <Lock className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm lg:text-base font-medium text-center">
              {isSpanish 
                ? '🔒 Confidencialidad garantizada: No recopilamos ningún dato. Todo se borra al recargar la página.'
                : '🔒 Confidentialité garantie : Nous ne collectons aucune donnée. Tout est effacé au rechargement de la page.'
              }
            </p>
          </div>
        </div>
      </section>

      {/* Formulaire optionnel */}
      <section className="py-8 bg-blue-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-blue-200 bg-white">
            <CardHeader>
              <CardTitle className="text-sage-900">
                {isSpanish 
                  ? '📝 Campos Opcionales (para personalizar automáticamente)'
                  : '📝 Champs Optionnels (pour personnaliser automatiquement)'
                }
              </CardTitle>
              <p className="text-sm text-sage-600 mt-2">
                {isSpanish
                  ? 'Rellena los campos que desees. Los modelos se actualizarán automáticamente.'
                  : 'Remplissez les champs que vous souhaitez. Les modèles se mettront à jour automatiquement.'
                }
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-sage-700 mb-1">
                    {isSpanish ? 'Tu nombre' : 'Votre prénom'}
                  </label>
                  <Input
                    type="text"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleInputChange}
                    placeholder={isSpanish ? 'Juan' : 'Marie'}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-sage-700 mb-1">
                    {isSpanish ? 'Tu apellido' : 'Votre nom'}
                  </label>
                  <Input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleInputChange}
                    placeholder={isSpanish ? 'García' : 'Dupont'}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-sage-700 mb-1">
                    {isSpanish ? 'Nombre del asesor' : 'Nom du conseiller'}
                  </label>
                  <Input
                    type="text"
                    name="conseiller"
                    value={formData.conseiller}
                    onChange={handleInputChange}
                    placeholder={isSpanish ? 'Sr. López' : 'M. Martin'}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-sage-700 mb-1">
                    {isSpanish ? 'N° de Contrato' : 'N° de Contrat'}
                  </label>
                  <Input
                    type="text"
                    name="numeroContrat"
                    value={formData.numeroContrat}
                    onChange={handleInputChange}
                    placeholder="123456789"
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-sage-700 mb-1">
                    {isSpanish ? 'N° de Cuenta' : 'N° de Compte'}
                  </label>
                  <Input
                    type="text"
                    name="numeroCompte"
                    value={formData.numeroCompte}
                    onChange={handleInputChange}
                    placeholder="987654321"
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-sage-700 mb-1">
                    {isSpanish ? 'N° de Póliza' : 'N° de Police'}
                  </label>
                  <Input
                    type="text"
                    name="numeroPolice"
                    value={formData.numeroPolice}
                    onChange={handleInputChange}
                    placeholder="POL123456"
                    className="w-full"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Lien Résiliation (FR uniquement) */}
      {!isSpanish && (
        <section className="py-4 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <ExternalLink className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-sage-900 text-sm lg:text-base">Besoin de résilier un contrat ?</h3>
                      <p className="text-sage-600 text-xs lg:text-sm">
                        Lettres de résiliation gratuites
                      </p>
                    </div>
                  </div>
                  <Button
                    asChild
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0"
                  >
                    <a 
                      href="https://www.lettre-resiliation.com/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2"
                    >
                      Lettres de Résiliation
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Templates */}
      <section className="py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {templatesData.templates.map((template) => (
              <Card key={template.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <CardHeader className={`bg-gradient-to-r ${template.color} text-white`}>
                  <CardTitle className="flex items-center gap-3">
                    <template.icon className="w-6 h-6" />
                    <div>
                      <div className="text-lg lg:text-xl">{template.title}</div>
                      <Badge className="bg-white/20 border-white/30 text-white text-xs mt-1">
                        {template.category}
                      </Badge>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {/* Objet */}
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs font-semibold text-gray-500 mb-1">
                      {isSpanish ? 'Asunto:' : 'Objet :'}
                    </div>
                    <div className="text-sm text-gray-900 font-medium">
                      {template.subject}
                    </div>
                  </div>

                  {/* Contenu */}
                  <div className="mb-4 p-4 bg-white border border-gray-200 rounded-lg">
                    <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 leading-relaxed">
                      {generateEmail(template.content)}
                    </pre>
                  </div>

                  {/* Conseils */}
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="text-xs font-semibold text-amber-800 mb-1">
                      💡 {isSpanish ? 'Consejo:' : 'Conseil :'}
                    </div>
                    <div className="text-sm text-amber-700">
                      {template.tips}
                    </div>
                  </div>

                  {/* Bouton Copier */}
                  <Button
                    onClick={() => copyToClipboard(template.content, template.id)}
                    className="w-full bg-sage-600 hover:bg-sage-700 text-white"
                  >
                    {copiedId === template.id ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        {isSpanish ? '¡Copiado!' : 'Copié !'}
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        {isSpanish ? 'Copiar modelo' : 'Copier le modèle'}
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
